import React, { Fragment, useCallback, useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Checkbox, Button, Slider } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Input as InputIcon,
} from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import DeleteIcon from "@material-ui/icons/Delete";
import { DataGridFooter } from "./DataGridFooter";
import { GridHeader } from "./GridHeader";
import { ServerPagination } from "./DataGridFooter/ServerPagination";
import Input from "../Input";

export default function Datagrid({
  renderRow,
  headings = [],
  data = [],
  selectable,
  selected = {},
  setSelected,
  editable,
  deletable,
  completable,
  exportable,
  link,
  title,
  actions,
  hideSelect,
  handleReject,
  expandable,
  handleExpand,
  expandHeader = "Modify",
  expandHeaderClassName = "column-center grid-header",
  expandIcon = (
    <EditIcon
      fontSize="small"
      style={{ color: "rgba(0, 0, 0, 0.54)" }}
      data-testid="expand"
    />
  ),
  withIcon,
  loading,
  sharable,
  handleshare,
  rejectable,
  handleDelete,
  paginationOptions = [5, 10, 20, 100],
  paginationDefaultValue = 10,
  renderHeaderWithSorting,
  headerClassName = "grid-header",
  serverPage,
  setServerPage,
  pageSize,
  setPageSize,
  isServerPagination,
  totalRecords = 0,
  editableCell,
  editableCellName,
  editableCellHeader,
  handleCellBlur,
  sliderCell,
  sliderCellName,
  sliderCelleader,
  handleSliderChange,
  sliderColor = "primary",
  extendable,
  defaultExtendable,
  renderExtendedRow,
  extendHeader,
  isNewTabLink,
  updateRow,
  footerRow,
  contentMinWidth = "700px",
}) {
  const [perPage, setPerPage] = useState(paginationDefaultValue);
  const [page, setPage] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [cellValue, setCellValue] = useState("");
  const [editableItem, setEditableItem] = useState("");
  const [extendedRows, setExtendedRows] = useState([]);

  // Common inline styles
  const gridWrapperStyle = {
    overflow: "auto",
    flexGrow: 1,
  };

  const gridTableStyle = {
    width: "100%",
    color: "#464e5f",
    backgroundColor: "transparent",
    borderCollapse: "collapse",
    textAlign: "left",
    minWidth: contentMinWidth,
  };

  const cursorPointerStyle = {
    cursor: "pointer",
  };

  const shareButtonStyle = {
    padding: "6px 14px",
    color: "#599D36",
    border: "1px solid #599D36",
    backgroundColor: "white",
    boxShadow:
      "0px 0px 2px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.24)",
    textTransform: "uppercase",
    borderRadius: "4px",
    cursor: "pointer",
    display: "inline-block",
    lineHeight: "1",
    minWidth: "100px",
    outline: "none",
    fontSize: "12px",
    fontWeight: "600",
  };

  const sliderCellStyle = {
    paddingRight: "10px",
  };

  // For Material-UI and complex structural styling, we still need to use a style tag
  // These are styles that can't easily be converted to inline styles
  const complexStyles = `
    .main-checkbox {
      padding-bottom: 14px;
      padding-left: 4px;
    }

    .table-row {
      border-bottom: 1px solid #ecf0f3;
    }

    .selectable {
      padding-left: 0.6rem;
      vertical-align: middle;
    }

    .selectable .MuiSvgIcon-root {
      width: 20px;
      height: 20px;
    }

    .selectable-header {
      padding: 0 0 0.5rem 0.6rem;
    }

    .selectable-header .MuiCheckbox-root {
      padding: 5px 9px 9px;
    }

    .completable .MuiSvgIcon-root {
      width: 20px;
      height: 20px;
      color: #00000060;
    }

    .grid-header {
      color: #9e9e9e;
      font-weight: 500;
      font-size: 14px;
      padding: 0 1.25rem 1rem;
      white-space: nowrap;
      text-align: left;
    }

    .editable-header {
      color: #9e9e9e;
      font-weight: 500;
      font-size: 14px;
      padding-bottom: 1rem;
      white-space: nowrap;
      text-align: left;
    }

    .nospaces-header {
      color: #9e9e9e;
      font-weight: 500;
      font-size: 14px;
      padding-bottom: 1rem;
      white-space: nowrap;
      text-align: left;
    }

    .column-center {
      text-align: center;
    }

    .action-icon {
      color: rgba(0, 0, 0, 0.54);
    }

    .certificate {
      margin-left: 10px;
    }
  `;

  const handleExtend = (id) => () => {
    if (extendedRows.includes(id)) {
      setExtendedRows((state) => state.filter((row) => row !== id));
    } else setExtendedRows([...extendedRows, id]);
  };

  const renderExtendIcon = (id) => {
    if (extendedRows.includes(id)) {
      return <RemoveIcon size="small" color="secondary" />;
    } else return <AddIcon size="small" />;
  };

  const show = isServerPagination
    ? data
    : data.slice((page - 1) * perPage, page * perPage);
  const allSelected = show.every(({ id }) => selected[id]);

  const renderHeader = (headings) => {
    return headings.map(([key, header]) => (
      <th key={key} className={headerClassName}>
        {header}
      </th>
    ));
  };

  const handleChangePerPage = (e) => {
    setPerPage(e.target.value);
    setPageSize && setPageSize(e.target.value);
    setPage(1);
    setServerPage && setServerPage(0);
  };

  const handleChangePage = (_, page) => {
    setPage(page);
    setServerPage && setServerPage(page);
  };
  const handleChangeSelected = useCallback(
    (id) =>
      setSelected({
        ...selected,
        [id]: !selected[id],
        // eslint-disable-next-line
      }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelected({});
    } else {
      setSelected(
        show.reduce((acc, item) => {
          acc[item.id] = true;
          return acc;
        }, {})
      );
    }
    // eslint-disable-next-line
  }, [allSelected, show]);

  const handleCellChange = (e) => {
    setCellValue(e.target.value);
  };

  const handleEditable = (id) => () => {
    setCellValue(
      (data.find((item) => (item || {}).id === id) || {})[editableCellName]
    );
    setEditMode(true);
    setEditableItem(id);
  };

  const onBlur = (cellValue, item) => {
    setEditMode(false);
    handleCellBlur(cellValue, item);
  };

  const renderCellValue = (value = "") => {
    if (!value) return value;
    else {
      return value.length > 15 ? value.slice(0, 15) + "..." : value;
    }
  };

  const renderEditableCell = (item = {}) => {
    const rowStyle = item[editableCellName]
      ? {
          paddingTop: "17px",
          paddingBottom: "17px",
          width: "150px",
          overflow: "hidden",
        }
      : {
          paddingTop: "26px",
          paddingBottom: "27px",
          width: "150px",
          overflow: "hidden",
        };
    return editMode && item.id === editableItem ? (
      <td style={{ width: "150px", overflow: "hidden" }}>
        <Input
          value={cellValue}
          onChange={handleCellChange}
          onBlur={() => onBlur(cellValue, item)}
          autoFocus
        />
      </td>
    ) : (
      <td style={rowStyle} onClick={handleEditable(item.id)}>
        {renderCellValue(item[editableCellName])}
      </td>
    );
  };

  const renderSliderCell = (item = {}) => {
    return (
      <td style={sliderCellStyle}>
        <Slider
          value={item[sliderCellName] || 0}
          onChange={(event, newValue) =>
            handleSliderChange(event, newValue, item)
          }
          size="small"
          valueLabelDisplay="auto"
          style={{ color: sliderColor }}
        />
      </td>
    );
  };

  const handleExtendedRow = (item, data, updateRow) => {
    if (defaultExtendable && renderExtendedRow) {
      return renderExtendedRow(item, data, updateRow);
    } else if (
      extendedRows.includes(item.id) &&
      !defaultExtendable &&
      renderExtendedRow
    )
      return renderExtendedRow(item, data, updateRow);
  };

  useEffect(() => {
    if (page > 1) {
      const currentPageData = isServerPagination
        ? data
        : data.slice((page - 1) * perPage, page * perPage);

      if (currentPageData.length === 0) {
        setPage(1);
        if (setServerPage) {
          setServerPage(0);
        }
      }
    }
  }, [data, page, perPage, isServerPagination, setServerPage]);

  return (
    <div>
      <style>{complexStyles}</style>
      {(title || actions) && <GridHeader title={title} actions={actions} />}
      <div style={gridWrapperStyle} data-testid="datagrid">
        <table style={gridTableStyle}>
          <thead>
            <tr className="table-row">
              {extendable && !defaultExtendable && <th>{extendHeader}</th>}
              {selectable && (
                <th className="main-checkbox column-center">
                  <Checkbox
                    checked={allSelected}
                    onChange={handleSelectAll}
                    data-testid="checkbox"
                  />
                </th>
              )}
              {withIcon && <th></th>}
              {renderHeaderWithSorting
                ? renderHeaderWithSorting(headings, data)
                : renderHeader(headings)}
              {sliderCell && (
                <th className="editable-header">{sliderCelleader}</th>
              )}
              {editableCell && (
                <th className="editable-header">{editableCellHeader}</th>
              )}
              {editable && <th className={headerClassName}>Edit</th>}
              {deletable && <th className={headerClassName}>Delete</th>}
              {rejectable && <th className={headerClassName}>Reject</th>}
              {completable && (
                <th className={`${headerClassName} column-center`}>Complete</th>
              )}
              {expandable && (
                <th className={`${expandHeaderClassName}`}>{expandHeader}</th>
              )}
              {sharable && <th className={headerClassName}>Share</th>}
            </tr>
          </thead>
          {loading ? (
            <tbody>
              <tr>
                {extendable && !defaultExtendable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {selectable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {withIcon && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {headings.map((item) => (
                  <td key={item[0]}>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                ))}
                {sliderCell && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {editableCell && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {editable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {deletable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {rejectable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {completable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {exportable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {expandable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
                {sharable && (
                  <td>
                    <Skeleton variant="rect" width={"100%"} height={520} />
                  </td>
                )}
              </tr>
            </tbody>
          ) : (
            <tbody>
              {show.map((item) => (
                <Fragment key={item.id}>
                  <tr className="table-row">
                    {extendable && !defaultExtendable && (
                      <td>
                        <span
                          style={cursorPointerStyle}
                          onClick={handleExtend(item.id)}
                        >
                          {renderExtendIcon(item.id)}
                        </span>
                      </td>
                    )}
                    {selectable && (
                      <td className="selectable column-center">
                        <Checkbox
                          checked={Boolean(selected[item.id])}
                          onChange={() => handleChangeSelected(item.id)}
                          aria-label="checkbox"
                        />
                      </td>
                    )}
                    {withIcon && <td>{item.icon}</td>}
                    {renderRow(headings, item)}
                    {sliderCell && renderSliderCell(item)}
                    {editableCell && renderEditableCell(item)}
                    {editable && (
                      <td>
                        <NavLink
                          to={`/${link}/${item.id}`}
                          data-testid="edit"
                          target={isNewTabLink ? "_blank" : "_self"}
                          rel="noopener noreferrer"
                        >
                          <Button className="action-button">
                            <EditIcon
                              fontSize="small"
                              className="action-icon"
                            />
                          </Button>
                        </NavLink>
                      </td>
                    )}
                    {deletable && (
                      <td>
                        <Button
                          className="action-button"
                          onClick={() => handleDelete(item.id)}
                        >
                          <DeleteIcon
                            fontSize="small"
                            color="primary"
                            data-testid="delete"
                            className="action-icon"
                          />
                        </Button>
                      </td>
                    )}
                    {rejectable && (
                      <td>
                        <Button
                          className="action-button"
                          onClick={() => handleReject(item.id)}
                        >
                          <CloseIcon
                            fontSize="small"
                            color="primary"
                            data-testid="delete"
                            className="action-icon"
                          />
                        </Button>
                      </td>
                    )}
                    {completable && (
                      <td className="completable column-center">
                        <div className="action-button">
                          <Checkbox data-testid="complete" />
                        </div>
                      </td>
                    )}
                    {exportable && (
                      <td>
                        <div className="action-button">
                          <InputIcon data-testid="export" />
                        </div>
                      </td>
                    )}
                    {expandable && (
                      <td>
                        <Button
                          className="action-button"
                          onClick={handleExpand(item.id)}
                        >
                          {expandIcon}
                        </Button>
                      </td>
                    )}
                    {sharable && (
                      <td>
                        <button
                          style={shareButtonStyle}
                          data-testid="share"
                          onClick={() => handleshare(item.id)}
                        >
                          Share
                        </button>
                      </td>
                    )}
                  </tr>
                  {handleExtendedRow(item, data, updateRow)}
                </Fragment>
              ))}
              {footerRow && footerRow(show)}
            </tbody>
          )}
        </table>
      </div>
      {isServerPagination ? (
        <ServerPagination
          handleChangePerPage={handleChangePerPage}
          perPage={pageSize}
          page={serverPage}
          handleChangePage={handleChangePage}
          loading={loading}
          data={show}
          totalRecords={totalRecords}
        />
      ) : (
        <DataGridFooter
          data={data}
          paginationOptions={paginationOptions}
          handleChangePerPage={handleChangePerPage}
          perPage={perPage}
          page={page}
          hideSelect={hideSelect}
          handleChangePage={handleChangePage}
        />
      )}
    </div>
  );
}
